import { useEffect, useMemo, useState } from 'react';
import {
  getPolyFeeSchedulesViaRelay,
  type PolyFeeSchedule,
} from '@/lib/polymarket/relay';

const KNOWN_TTL_MS = 60 * 60 * 1000;
const UNKNOWN_TTL_MS = 60 * 1000;
const BATCH_SIZE = 100;

interface CacheEntry {
  schedule: PolyFeeSchedule | null;
  fetchedAt: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<void>>();

function isFresh(entry: CacheEntry | undefined, now: number): boolean {
  if (!entry) return false;
  return now - entry.fetchedAt < (entry.schedule == null ? UNKNOWN_TTL_MS : KNOWN_TTL_MS);
}

/** Condition-id keyed schedules shared by the trade ticket and every open
 * position surface. Missing map key means loading; a present null means the
 * relay could not establish the schedule and the UI must not assume zero. */
export function usePolyFeeSchedules(conditionIds: readonly string[]): {
  schedules: ReadonlyMap<string, PolyFeeSchedule | null>;
  loading: boolean;
  error: string | null;
} {
  const key = useMemo(
    () => [...new Set(conditionIds.map((id) => id.trim()).filter(Boolean))].sort().join('|'),
    [conditionIds],
  );
  const ids = useMemo(() => (key ? key.split('|') : []), [key]);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ids.length === 0) return;
    const now = Date.now();
    const missing = ids.filter((id) => !isFresh(cache.get(id), now));
    if (missing.length === 0) return;
    const requestKey = missing.join('|');
    let request = inFlight.get(requestKey);
    if (!request) {
      const batches: string[][] = [];
      for (let index = 0; index < missing.length; index += BATCH_SIZE) {
        batches.push(missing.slice(index, index + BATCH_SIZE));
      }
      request = Promise.all(batches.map((batch) => getPolyFeeSchedulesViaRelay(batch))).then((outputs) => {
        const fetchedAt = Date.now();
        for (const out of outputs) {
          if (!out.ok) throw new Error(out.error);
          for (const [id, schedule] of Object.entries(out.schedules)) {
            cache.set(id, { schedule, fetchedAt });
          }
        }
        for (const id of missing) {
          if (!cache.has(id)) cache.set(id, { schedule: null, fetchedAt });
        }
      }).finally(() => inFlight.delete(requestKey));
      inFlight.set(requestKey, request);
    }
    let cancelled = false;
    request
      .then(() => {
        if (!cancelled) {
          setError(null);
          setVersion((value) => value + 1);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [ids]);

  return useMemo(() => {
    void version;
    const schedules = new Map<string, PolyFeeSchedule | null>();
    for (const id of ids) {
      const entry = cache.get(id);
      if (entry) schedules.set(id, entry.schedule);
    }
    return { schedules, loading: schedules.size < ids.length, error };
  }, [ids, version, error]);
}
